/**
 * PRO-1 — Cabinet home.
 *
 * Two things carry real weight here:
 *
 * 1. THE STATUS BANNER. There is NO `rejected` status (docs/04 §1.1) — a rejected producer stays
 *    `pending_approval` with `rejection_reason` set. So the banner is driven by the REASON, not
 *    by the status:
 *      • pending_approval + no reason  → neutral "awaiting MOST approval"
 *      • pending_approval + reason set → amber rejection banner, the admin's reason VERBATIM,
 *                                        and a CTA to PRO-2 to fix and re-submit
 *      • active                        → no banner
 *    Re-submitting clears `rejection_reason` server-side and the banner reverts to neutral.
 *
 * 2. THE TELEGRAM CARD. Telegram is a first-class channel because email — especially Google — is
 *    unreliable in Turkmenistan (CLAUDE.md). But an UNLINKED Telegram is never an error: delivery
 *    falls back silently to email + in-app, and the card says so plainly rather than nagging.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Boxes, ClipboardList, Clock, FileEdit, Send } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardsSkeleton,
  ConfirmDialog,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components/ui';
import { useMe } from '@/shared/session';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { formatDate } from '@/shared/money';

export function HomeScreen() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();

  const profile = useQuery({ queryKey: qk.profile, queryFn: api.getProfile });

  // Quick stats. `total` from a per_page=1 page is the cheapest way to count without a dedicated
  // endpoint — there is no `GET /producer/dashboard` in the contract (noted in the handover).
  const activeProducts = useQuery({
    queryKey: qk.products({ status: 'active', per_page: 1 }),
    queryFn: () => api.listProducts({ status: 'active', per_page: 1 }),
  });
  const draftProducts = useQuery({
    queryKey: qk.products({ status: 'draft', per_page: 1 }),
    queryFn: () => api.listProducts({ status: 'draft', per_page: 1 }),
  });
  const orders = useQuery({
    queryKey: qk.orders({ per_page: 25 }),
    queryFn: () => api.listOrders({ per_page: 25 }),
  });

  // "New order slices" = slices whose shared order status is still `new`.
  const newOrders = orders.data?.items.filter((o) => o.status === 'new').length ?? 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('home.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('home.greeting')}</p>
      </div>

      <StatusBanner
        status={me?.status}
        rejectionReason={profile.data?.rejection_reason ?? null}
        loading={profile.isLoading}
      />

      {/* ── Quick stats ── */}
      {activeProducts.isLoading || draftProducts.isLoading || orders.isLoading ? (
        <CardsSkeleton count={3} />
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            icon={Boxes}
            label={t('home.stats.activeProducts')}
            value={activeProducts.data?.total ?? 0}
            to="/products?status=active"
          />
          <StatCard
            icon={FileEdit}
            label={t('home.stats.draftProducts')}
            value={draftProducts.data?.total ?? 0}
            to="/products?status=draft"
          />
          <StatCard
            icon={ClipboardList}
            label={t('home.stats.newOrders')}
            value={newOrders}
            to="/orders"
          />
        </div>
      )}

      <TelegramCard locale={i18n.language} />
    </div>
  );
}

// ── Status banner ───────────────────────────────────────────────────────────────────────────
function StatusBanner({
  status,
  rejectionReason,
  loading,
}: {
  status: string | undefined;
  rejectionReason: string | null;
  loading: boolean;
}) {
  const { t } = useTranslation();

  if (loading) return <Skeleton className="h-24 w-full rounded-lg" />;

  // `active` → no banner at all. The absence IS the signal.
  if (status !== 'pending_approval') return null;

  // The ONLY thing distinguishing "awaiting first review" from "changes requested".
  const rejected = Boolean(rejectionReason && rejectionReason.trim());

  if (rejected) {
    return (
      <Card className="border-amber-300 bg-amber-50" role="status">
        <CardContent className="flex items-start gap-3 p-5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <div className="flex-1">
            <p className="font-medium text-amber-900">{t('home.rejectedTitle')}</p>
            <p className="mt-2 text-sm font-medium text-amber-900">{t('home.rejectedBody')}</p>
            {/* The admin's reason, VERBATIM. Not paraphrased, not summarized — the producer has
                to know exactly what to fix. `whitespace-pre-wrap` preserves their line breaks. */}
            <blockquote className="mt-1 whitespace-pre-wrap rounded-md border border-amber-200 bg-card px-3 py-2 text-sm text-foreground">
              {rejectionReason}
            </blockquote>
            {/* A real <a>, not a <button> wrapping one: this navigates, so it must be
                middle-clickable and keyboard-activatable as a link. */}
            <Link
              to="/profile"
              className="mt-3 inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t('home.rejectedCta')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-input bg-muted/70" role="status">
      <CardContent className="flex items-start gap-3 p-5">
        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
        <div>
          <p className="font-medium text-foreground">{t('home.pendingTitle')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('home.pendingBody')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Stat card ───────────────────────────────────────────────────────────────────────────────
function StatCard({
  icon: Icon,
  label,
  value,
  to,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Card className="transition-colors hover:border-muted-foreground/40">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ── Telegram card ───────────────────────────────────────────────────────────────────────────
function TelegramCard({ locale }: { locale: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();

  // While a link is pending we poll: the actual linking happens OUT OF BAND (the user taps the
  // deep link, Telegram sends `/start <token>` to the bot, the bot's long-poll captures their
  // chat_id). The browser is never told directly — polling is the only way we learn it worked.
  const [awaitingLink, setAwaitingLink] = React.useState(false);
  const [confirmUnlink, setConfirmUnlink] = React.useState(false);

  const status = useQuery({
    queryKey: qk.telegramStatus,
    queryFn: api.getTelegramStatus,
    refetchInterval: awaitingLink ? 3000 : false,
  });

  const linked = status.data?.linked ?? false;

  // Stop polling the moment it flips to linked.
  React.useEffect(() => {
    if (linked && awaitingLink) {
      setAwaitingLink(false);
      toast(t('home.telegram.linked'), 'success');
    }
  }, [linked, awaitingLink, toast, t]);

  const mintToken = useMutation({
    mutationFn: api.createTelegramLinkToken,
    onSuccess: (data) => {
      // The token is single-use with a 10-minute TTL, so open it immediately.
      window.open(data.deep_link_url, '_blank', 'noopener,noreferrer');
      setAwaitingLink(true);
    },
    onError: () => toast(t('home.telegram.openFailed'), 'error'),
  });

  const unlink = useMutation({
    mutationFn: api.unlinkTelegram,
    onSuccess: () => {
      setConfirmUnlink(false);
      void qc.invalidateQueries({ queryKey: qk.telegramStatus });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });

  if (status.isError) {
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-muted-foreground" aria-hidden />
            <CardTitle>{t('home.telegram.title')}</CardTitle>
            {linked ? <Badge tone="green">{t('home.telegram.linked')}</Badge> : null}
          </div>
        </CardHeader>

        <CardContent>
          {status.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : linked ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                {status.data?.linked_at
                  ? t('home.telegram.linkedSince', {
                      date: formatDate(status.data.linked_at, locale),
                    })
                  : t('home.telegram.why')}
              </p>
              <Button variant="outline" size="sm" onClick={() => setConfirmUnlink(true)}>
                {t('home.telegram.unlink')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t('home.telegram.why')}</p>

              {/* The silent fallback, stated plainly. An unlinked Telegram degrades the channel,
                  it does not break the flow — so this is a hint, not a warning. */}
              <p className="rounded-md border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                {t('home.telegram.fallbackHint')}
              </p>

              <div className="flex items-center gap-3">
                <Button onClick={() => mintToken.mutate()} loading={mintToken.isPending}>
                  <Send className="h-4 w-4" aria-hidden />
                  {t('home.telegram.connect')}
                </Button>
                {awaitingLink ? (
                  <p className="text-xs text-muted-foreground" aria-live="polite">
                    {t('home.telegram.waiting')}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink}
        onClose={() => setConfirmUnlink(false)}
        onConfirm={() => unlink.mutate()}
        loading={unlink.isPending}
        title={t('home.telegram.unlink')}
        description={t('home.telegram.fallbackHint')}
        confirmLabel={t('home.telegram.unlink')}
        destructive
      />
    </>
  );
}
